/**
 * MathJSON, on demand (SPEC §4.1, §16).
 *
 * The answer travels as MathJSON, converted deterministically to SymPy server
 * -side; LaTeX travels beside it for display and archiving. MathLive only emits
 * MathJSON once the CortexJS Compute Engine is loaded, and that library is over
 * a megabyte — loading it during first paint on a tablet would cost exactly the
 * seconds this component is meant to save.
 *
 * So it is fetched lazily, and its absence is never fatal: a submission carries
 * `mathjson: null` and the LaTeX, which the server can still parse. Being late
 * is acceptable; blocking a child's first exercise is not.
 */

import type { MathfieldElement } from "mathlive";

let loading: Promise<boolean> | null = null;

export function computeEngineReady(): boolean {
  return Boolean((globalThis as Record<symbol, unknown>)[Symbol.for("io.cortexjs.compute-engine")]);
}

/** Starts the download. Safe to call repeatedly; only the first one works. */
export function loadComputeEngine(): Promise<boolean> {
  if (computeEngineReady()) return Promise.resolve(true);
  loading ??= import("@cortex-js/compute-engine")
    .then(() => true)
    .catch(() => false);
  return loading;
}

/** Call once the shell is idle: the exercise screen must not wait for it. */
export function warmComputeEngine(): void {
  const start = () => void loadComputeEngine();
  const idle = (globalThis as { requestIdleCallback?: (callback: () => void) => void })
    .requestIdleCallback;
  if (idle) idle(start);
  else setTimeout(start, 2_000);
}

export type MathAnswer = {
  latex: string;
  /** A MathJSON tree, serialised. `null` when the engine has not arrived yet. */
  mathjson: string | null;
};

export function readAnswer(field: MathfieldElement): MathAnswer {
  const latex = field.getValue("latex");
  if (!computeEngineReady()) return { latex, mathjson: null };
  try {
    const mathjson = field.getValue("math-json");
    return { latex, mathjson: mathjson || null };
  } catch {
    return { latex, mathjson: null };
  }
}
